import * as React from "react";

import {Invoke}                                         from "@Common/react/invoke";
import {Load2}                                          from "@Common/react/load2";
import {Rerender}                                       from "@Common/react/rerender";
import {UseEffect}                                      from "@Common/react/use-effect";
import {Types}                                          from "@Common/types";
import {URL_QUERY_KEY as MODAL_SERVICE_URL_QUERY_KEY}   from "@Common/ui-components/modal-service-3/modal-service-3";

import {
    ComponentLink,
    Link,
    makeChain,
    valueHelper as value,
} from "@Common/react/cst2";

import {consumeContext} from "@Xpresso/loaders/consume-context";

type Popup = Types.Apis.Models.PopupModal;

const LOCATION_POLLING_PERIOD_MS                = 100;
const LOCATION_TIMES:{[location:string]: number}= {};
const LOCATION_WAIT_PERIOD_SECS                 = 60;
const POPUP_WAIT_PERIOD_SECS                    = 60*60*24;
const STORAGE_KEY_TEMPLATE                      = "user[ID_USER].popupsSeenIds";
const URL_QUERY_KEY                             = "popup";

let pathJustUsed:string = null;
export const PopupsService = ({next: rootNext}:{
    next    : Link<JSX.Element, {
        currentPopup    : Popup,
        consume         : (permanently?: boolean) => void,
        dismiss         : (permanently?: boolean) => void,
    }>,
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then(...consumeContext("userLevels"))

    .then("storageAccess", value(({userLevels}) =>
    {
        const storageKey = STORAGE_KEY_TEMPLATE.replace("ID_USER", userLevels?.idUser?.toString() ?? "0");
        return makeStorageAccess(storageKey);
    }))

    .then("popupsLoad", ({apis}, next:ComponentLink<typeof Load2<Popup[]>>) => (
        Load2({fetch: apis.next.getPopupModals, next})
    ))
    .then("rerender", ({}, next:ComponentLink<typeof Rerender>) => Rerender({next: next}))
    .then("currentPopupState", ({popupsLoad: {value: popups}, rerender}, next:Link<JSX.Element, {
        value       : Popup,
        setValue    : (pup:Popup) => void,
    }>) =>
    {
        const id        = Number(new URLSearchParams(window.location.search).get(URL_QUERY_KEY) ?? 0);
        return next({
            value       : popups?.find(pup => pup.id === id),
            setValue    : (pup) =>
            {
                const url = new URL(window.location.toString());
                if (pup) {
                    url.searchParams.set(URL_QUERY_KEY, pup.id.toString());
                } else {
                    url.searchParams.delete(URL_QUERY_KEY);
                }
                window.history.replaceState(window.history.state, "", url);
                rerender.forceUpdate();
            },
        });
    })
    .then(({rerender}, next:Link<JSX.Element>) => (
        Invoke({
            next,
            fn: ({isMounted}) =>
            {
                let location = window.location.toString();

                const intervalId = window.setInterval(
                    () => (
                        isMounted()
                        ? (
                            window.location.toString() !== location
                            && (location=window.location.toString(), rerender.forceUpdate())
                        )
                        : window.clearInterval(intervalId)
                    ),
                    LOCATION_POLLING_PERIOD_MS
                );
            },
        })
    ))
    .then("otherPopupShowing", value(() => (
        !!(new URLSearchParams(window.location.search).get(MODAL_SERVICE_URL_QUERY_KEY))
    )))
    .then(({
        currentPopupState: {setValue: setCurrentPopup, value: currentPopup},
        otherPopupShowing,
        popupsLoad: {value: popups},
        storageAccess: {clearPupTime, getAlivePupTimes},
    }, next: Link<JSX.Element>) => (
        <UseEffect next={next} fn={() =>
        {
            if (currentPopup && otherPopupShowing) {
                setCurrentPopup(null);
                clearPupTime(currentPopup.id);
            }
            if (currentPopup || otherPopupShowing) return;

            if (pathJustUsed === window.location.pathname) return;
            pathJustUsed = null;

            const now       = Math.floor((new Date).valueOf() / 1000);
            const pupTimes  = getAlivePupTimes();
            const nextPup   = popups?.filter(pup =>
            {
                const pupSeenRecently       = pup.id in pupTimes;
                const pupLocationMatches    = new RegExp(pup.targetUrlRegex).test(window.location.href)
                const locationIsReady       = (
                    LOCATION_WAIT_PERIOD_SECS > (now - (LOCATION_TIMES[pup.targetUrlRegex] ?? now))
                );
                return !pupSeenRecently && pupLocationMatches && locationIsReady;
            })[0];

            if (nextPup) {
                setCurrentPopup(nextPup);
                pathJustUsed                            = window.location.pathname;
                LOCATION_TIMES[nextPup.targetUrlRegex]  = now;
            }
        }}/>
    ))
    .then(({
        apis,
        currentPopupState: {setValue: setCurrentPopup, value: currentPopup},
        otherPopupShowing,
        storageAccess: {setPupTimeNow},
    }) => {
        const consume = (permanently:boolean) =>
        {
            if (!currentPopup) return;
            if (permanently) {
                apis.next.permanentlyDismissPopup(currentPopup.id);
            }
            setPupTimeNow(currentPopup.id);
        };
        return rootNext({
            consume,
            currentPopup: !otherPopupShowing ? currentPopup : null,
            dismiss: (permanently) =>
            {
                if (!currentPopup) return;
                consume(permanently);
                setCurrentPopup(null);
            }
        });
    })
    .run()
);

const makeStorageAccess = (key:string) =>
{
    const clearPupTime = (id:number) => window.localStorage.setItem(key, JSON.stringify({
        ...(({[id]: _, ...pupTimes}) => pupTimes)(getAlivePupTimes())
    }));

    const getAlivePupTimes = ():{[id:number]: number} =>
    {
        const now = getTimeSecondsNow();
        return Object.fromEntries(
            Object.entries(getPupTimes())
            .filter(([,time]) => POPUP_WAIT_PERIOD_SECS > now - time)
        );
    };

    const getPupTimes = ():{[id:number]: number} => JSON.parse(
        window.localStorage.getItem(key) ?? "{}"
    );

    const setPupTimeNow = (id:number) => window.localStorage.setItem(key, JSON.stringify({
        ...getAlivePupTimes(),
        [id]: getTimeSecondsNow(),
    }));

    return {clearPupTime, getAlivePupTimes, getPupTimes, setPupTimeNow};
};

const getTimeSecondsNow = () => Math.floor((new Date).valueOf() / 1000);

